// MainLayout.js
import React, { useState, useEffect } from "react";
import Sidebar from "./sidebar/Sidebar";
import { Outlet } from "react-router-dom";
import Header from "./header/Header";
import BreadCrumb from "../../components/BreadCrumb";
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  CssBaseline,
  useTheme,
  useMediaQuery,
  styled,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const drawerWidth = 260;

// This button is styled to be smaller and more like the Bitbucket example.
const SidebarToggleButton = styled(IconButton)(({ theme, open }) => ({
  // --- Size & Shape ---
  width: "28px", // Making the button smaller
  height: "28px", // Making the button smaller

  // --- Position ---
  position: "fixed",
  top: "72px", // Positioned just below the 64px AppBar
  left: open ? drawerWidth : `calc(${theme.spacing(8)} + 1px)`,
  transform: "translateX(-50%)",
  zIndex: theme.zIndex.drawer + 2,

  // --- Style ---
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[2], // Using a more subtle shadow

  // --- Transitions & Hover ---
  transition: theme.transitions.create(
    ["left", "transform", "background-color"],
    {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }
  ),
  "&:hover": {
    backgroundColor:
      theme.palette.mode === "light"
        ? theme.palette.grey[100]
        : theme.palette.grey[800],
    transform: "translateX(-50%) scale(1.1)",
  },
}));

const MainLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [isSidebarOpen, setIsSidebarOpen] = useState(!isMobile);

  useEffect(() => {
    setIsSidebarOpen(!isMobile);
  }, [isMobile]);

  const handleSidebarToggle = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleSidebarCloseMobile = () => {
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  };

  return (
    <>
      <SidebarToggleButton
        onClick={handleSidebarToggle}
        open={isSidebarOpen}
        sx={{ display: { xs: "none", md: "inline-flex" } }}
      >
        {/* Using sx prop to reduce icon size to fit the smaller button */}
        {isSidebarOpen ? (
          <ChevronLeftIcon sx={{ fontSize: "1.2rem" }} />
        ) : (
          <ChevronRightIcon sx={{ fontSize: "1.2rem" }} />
        )}
      </SidebarToggleButton>

      <Box
        sx={{
          display: "flex",
          minHeight: "100vh",
          bgcolor: theme.palette.background.default,
        }}
      >
        <CssBaseline />
        <AppBar
          position="fixed"
          elevation={1}
          sx={{
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            transition: theme.transitions.create(["width", "margin"], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
            ...(isSidebarOpen &&
              !isMobile && {
                width: `calc(100% - ${drawerWidth}px)`,
                marginLeft: `${drawerWidth}px`,
                transition: theme.transitions.create(["width", "margin"], {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
                }),
              }),
          }}
        >
          <Toolbar sx={{ minHeight: "64px !important" }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleSidebarToggle}
              edge="start"
              sx={{
                marginRight: 2,
                display: { xs: "inline-flex", md: "none" },
              }}
            >
              <MenuIcon />
            </IconButton>
            <Box sx={{ flexGrow: 1 }} />
            <Header />
          </Toolbar>
        </AppBar>

        <Sidebar
          open={isSidebarOpen}
          variant={isMobile ? "temporary" : "permanent"}
          onClose={handleSidebarToggle}
          onListItemClick={handleSidebarCloseMobile}
          ModalProps={{
            keepMounted: true,
          }}
        />

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            pt: `${64 + parseInt(theme.spacing(2))}px`,
            px: { xs: 2, sm: 3 },
            pb: theme.spacing(3),
            overflow: "auto",
          }}
        >
          <BreadCrumb />
          <Outlet />
        </Box>
      </Box>
    </>
  );
};

export default MainLayout;
